import { Dayjs } from 'dayjs';
import { useCallback, useMemo } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { groupBy, prop, range } from 'remeda';
import { useDebounce } from 'src/utils/hooks/use-debounce';
import { TIME_CHANGE_DEBOUNCE_DELAY_MS, WeekScheduleFormFields } from './const';
import { TimeRangeInput } from './time-range-input';

export type TimeRangeValue = {
  weekDay: number;
  id: string;
  from: string;
  to: string;
  createdAt: string;
};

export type WeekScheduleFormValues = {
  [WeekScheduleFormFields.Schedule]: TimeRangeValue[];
};

export const WeekSchedule = <T extends Record<string, unknown>>({
  values,
  turnedOffDateLabel,
  maxTimeWindows,
  mapEntityToFormValue,
  onDeleteTimeWindow,
  onAddTimeWindow,
  onChangeTimeWindow,
}: {
  values: WeekScheduleFormValues;
  turnedOffDateLabel?: string;
  maxTimeWindows?: number;
  onDeleteTimeWindow: (ids: string | string[]) => void;
  onAddTimeWindow: (dayIndex: number) => {
    create: () => Promise<T>;
    optimisticResponse: T;
  };
  onChangeTimeWindow: (id: string, inputType: 'from' | 'to', time: Dayjs | null) => void;
  mapEntityToFormValue: (entity: T) => TimeRangeValue;
}) => {
  const formMethods = useForm<WeekScheduleFormValues>({
    defaultValues: values,
  });

  const { getValues, control } = formMethods;

  const {
    fields: scheduleFields,
    insert,
    remove,
    update,
  } = useFieldArray({
    keyName: '_id',
    control,
    name: WeekScheduleFormFields.Schedule,
  });

  const groupedFields = useMemo(
    () =>
      groupBy(
        scheduleFields.map((it, index) => ({ ...it, index })),
        prop('weekDay'),
      ),
    [scheduleFields],
  );

  const onTimeAdd = useCallback(
    async (dayIndex: number) => {
      const { create, optimisticResponse } = onAddTimeWindow(dayIndex);

      if (optimisticResponse) {
        const insertedIndex = scheduleFields.length;
        insert(insertedIndex, mapEntityToFormValue(optimisticResponse));
        const result = await create();
        if (result) {
          // replace optimistic temp id with a real one
          update(insertedIndex, mapEntityToFormValue(result));
        }
      }
    },
    [onAddTimeWindow, scheduleFields.length, insert, mapEntityToFormValue, update],
  );

  const onDayToggleOn = useCallback(
    (dayIndex: number) => {
      void onTimeAdd(dayIndex);
    },
    [onTimeAdd],
  );

  const onDayToggleOff = useCallback(
    (dayIndex: number) => {
      const items = groupedFields[dayIndex] || [];
      const ids = items.map((it) => it.id);
      const indexes = items.map((it) => it.index);
      if (ids.length) {
        onDeleteTimeWindow(ids);
        remove(indexes);
      }
    },
    [groupedFields, onDeleteTimeWindow, remove],
  );

  const onTimeDelete = useCallback(
    (index: number) => {
      const id = getValues(`${WeekScheduleFormFields.Schedule}.${index}.id`);
      if (id) {
        onDeleteTimeWindow(id);
        remove(index);
      }
    },
    [getValues, onDeleteTimeWindow, remove],
  );

  const onTimeChange = useDebounce(
    (id: string, inputType: 'from' | 'to', time: Dayjs | null) => {
      onChangeTimeWindow(id, inputType, time);
    },
    TIME_CHANGE_DEBOUNCE_DELAY_MS,
  );

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={(e) => e?.preventDefault()}>
        {range(1, 8).map((index) => {
          // Sunday should goes last but has index 0
          const dayIndex = index === 7 ? 0 : index;
          return (
            <TimeRangeInput
              fieldName={WeekScheduleFormFields.Schedule}
              dayIndex={dayIndex}
              key={dayIndex}
              items={groupedFields[dayIndex]}
              maxTimeWindows={maxTimeWindows}
              onToggleOn={onDayToggleOn}
              onToggleOff={onDayToggleOff}
              onTimeChange={onTimeChange}
              onTimeAdd={onTimeAdd}
              onTimeDelete={onTimeDelete}
              turnedOffDateLabel={turnedOffDateLabel}
            />
          );
        })}
      </form>
    </FormProvider>
  );
};
